"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { TabButton } from "@components/molecules/TabButton";
import { CalculatorForm } from "@components/molecules/CalculatorForm";
import { CalculatorResult } from "@components/molecules/CalculatorResult";
import Button from "@components/atoms/Button";
import Text from "@components/atoms/Text";
import {
  EMI_DEFAULT_VALUE,
  EMI_MAX,
  EMI_MIN,
  EMI_STEP,
  AVERAGE_PERCENTAGE,
  AVERAGE_TENURE,
  INTEREST_MAX,
  INTEREST_MIN,
  INTEREST_STEP,
  TAB_KEYS,
  TAB_LABELS,
  TENURE_OPTIONS,
} from "../constants";
import {
  calculateEMIFromLoanAmount,
  calculateLoanAmountFromEMI,
} from "../utils";

import {
  CalculatorFormData,
  ResultData,
  TabType,
} from "../EMICalculator.types";

import styles from "./EMICalculatorClientWrapper.module.scss";
import { carSearchService } from "@services/carSearchService";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";

const EMICalculatorClientWrapper = ({
  onRoadMinprice,
  onRoadMaxPrice,
  cityName,
  slug,
  model,
  brand,
  interestRate = AVERAGE_PERCENTAGE,
  isLoggedIn,
  carAvailability,
  pageType,
}: any) => {
  const parsedInterestRate = Number(interestRate);
  const resolvedInterestRate =
    Number.isFinite(parsedInterestRate) && parsedInterestRate > 0
      ? parsedInterestRate
      : AVERAGE_PERCENTAGE;
  const resolvedOnRoadMinPrice = Math.max(Number(onRoadMinprice) || 0, 0);
  const resolvedOnRoadMaxPrice = Math.max(
    Number(onRoadMaxPrice) || resolvedOnRoadMinPrice,
    resolvedOnRoadMinPrice
  );
  const minDownPayment = useMemo(
    () => Math.round((resolvedOnRoadMinPrice * 0.1) / 1000) * 1000,
    [resolvedOnRoadMinPrice]
  );
  const maxDownPayment = useMemo(
    () => Math.round((resolvedOnRoadMaxPrice * 0.9) / 1000) * 1000,
    [resolvedOnRoadMaxPrice]
  );
  const minLoanAmount = minDownPayment;
  const maxLoanAmount = maxDownPayment;
  const defaultLoanAmount = Math.min(
    maxLoanAmount,
    Math.max(minLoanAmount, resolvedOnRoadMinPrice - minDownPayment)
  );

  const [activeTab, setActiveTab] = useState<TabType>(TAB_KEYS.LOAN);
  const [emiForm, setEmiForm] = useState<CalculatorFormData>({
    emi: {
      value: EMI_DEFAULT_VALUE,
      min: EMI_MIN,
      max: EMI_MAX,
      step: EMI_STEP,
    },
    downPayment: {
      value: minDownPayment,
      min: minDownPayment,
      max: maxDownPayment,
      step: 1000,
    },
    interest: {
      value: resolvedInterestRate,
      min: INTEREST_MIN,
      max: INTEREST_MAX,
      step: INTEREST_STEP,
    },
    tenure: AVERAGE_TENURE,
  });
  const [loanForm, setLoanForm] = useState<CalculatorFormData>({
    loan: {
      value: defaultLoanAmount,
      min: minLoanAmount,
      max: maxLoanAmount,
      step: 1000,
    },

    downPayment: {
      value: minDownPayment,
      min: minDownPayment,
      max: maxDownPayment,
      step: 1000,
    },

    interest: {
      value: resolvedInterestRate,
      min: INTEREST_MIN,
      max: INTEREST_MAX,
      step: INTEREST_STEP,
    },
    tenure: AVERAGE_TENURE,
  });
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState<ResultData | null>(null);
  const calculationVersionRef = useRef(0);

  const activeForm = useMemo(
    () => (activeTab === TAB_KEYS.EMI ? emiForm : loanForm),
    [activeTab, emiForm, loanForm]
  );

  const setActiveForm = activeTab === TAB_KEYS.EMI ? setEmiForm : setLoanForm;

  useEffect(() => {
    calculationVersionRef.current += 1;
    setShowResult(false);
  }, [activeForm]);

  const handleCalculation = async () => {
    const calculationVersion = calculationVersionRef.current;

    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_emi",
        selected_value:
          activeTab === TAB_KEYS.EMI ? "EMI I can pay" : "Loan amount I need",
        car_availability: carAvailability,
        city_name: cityName,
        vehicle_make: brand,
        vehicle_model: model,
      },
    });

    const { interest, tenure } = activeForm;
    let maxPrice = 0,
      variantsCount = 0;
    if (activeTab === TAB_KEYS.EMI && activeForm.emi) {
      const loanAmount = calculateLoanAmountFromEMI(
        activeForm?.emi?.value,
        interest?.value,
        tenure
      );

      maxPrice = loanAmount + activeForm?.downPayment?.value;
      variantsCount = await fetchVariantCount(cityName, maxPrice, slug);

      setResult({
        calculatedLoanAmount: loanAmount,
        interest: interest?.value,
        emi: activeForm?.emi?.value,
        downpayment: activeForm?.downPayment?.value,
        maxPrice: maxPrice,
        variantsCount: variantsCount,
      });
    } else if (activeForm.loan) {
      const EMI = calculateEMIFromLoanAmount(
        activeForm?.loan?.value,
        interest?.value,
        tenure
      );

      maxPrice = activeForm?.loan?.value + activeForm?.downPayment?.value;
      const variantsCount = await fetchVariantCount(cityName, maxPrice, slug);

      setResult({
        calculatedEMI: EMI,
        interest: interest?.value,
        loan: activeForm?.loan?.value,
        downpayment: activeForm?.downPayment?.value,
        maxPrice: maxPrice,
        variantsCount: variantsCount,
      });
    }

    if (calculationVersion !== calculationVersionRef.current) return;
    setShowResult(true);
  };

  return (
    <div className={styles.wrapper}>
      <div>
        <Text className={styles.heading}>
          Let&apos;s begin with what you have in mind
        </Text>

        <div className={styles.tabs}>
          {Object.entries(TAB_LABELS).map(([key, label]) => (
            <TabButton
              key={key}
              isActive={activeTab === key}
              label={label}
              onClick={() => {
                setActiveTab(key as TabType);
                setShowResult(false);
              }}
            />
          ))}
        </div>

        <Text className={styles.subtext}>
          {activeTab === TAB_KEYS.EMI
            ? "We’ll show you how much loan you can get based on your preferred monthly payment"
            : "Let's calculate the EMI based on your desired loan amount"}
        </Text>
      </div>

      <CalculatorForm
        activeTab={activeTab}
        formData={activeForm}
        tenureOptions={TENURE_OPTIONS}
        onEmiChange={(val) =>
          setEmiForm((prev) => ({
            ...prev,
            emi: { ...prev.emi!, value: val },
          }))
        }
        onLoanChange={(val) =>
          setLoanForm((prev) => ({
            ...prev,
            loan: { ...prev.loan!, value: val },
          }))
        }
        onDownPaymentChange={(val) =>
          setActiveForm((prev) => ({
            ...prev,
            downPayment: { ...prev.downPayment, value: val },
          }))
        }
        onInterestChange={(val) =>
          setActiveForm((prev) => ({
            ...prev,
            interest: { ...prev.interest, value: val },
          }))
        }
        onTenureChange={(val) =>
          setActiveForm((prev) => ({ ...prev, tenure: val }))
        }
      />

      <Button
        size="ds"
        className={styles.calculateButton}
        onButtonClick={handleCalculation}
        ariaLabel={
          activeTab === TAB_KEYS.EMI ? "Calculate loan amount" : "Calculate EMI"
        }
      >
        {activeTab === TAB_KEYS.EMI
          ? "Calculate my loan amount"
          : "Calculate my EMI"}
      </Button>

      {showResult && result && (
        <CalculatorResult
          activeTab={activeTab}
          result={result}
          formData={activeForm}
          onSeeOptions={() => console.log("See options clicked")}
          slug={slug}
          model={model}
          brand={brand}
          cityName={cityName}
          carAvailability={carAvailability}
          pageType={pageType}
        />
      )}
    </div>
  );
};

export default EMICalculatorClientWrapper;
async function fetchVariantCount(cityName: any, maxPrice: number, slug: any) {
  let variantsCount = 0;

  try {
    const searchCarsRes = await carSearchService.searchCars({
      city: cityName,
      minPrice: `0`,
      maxPrice: `${maxPrice}`,
      slug: slug,
    });

    variantsCount = searchCarsRes?.result?.[0]?.variant_count;
  } catch (error) {
    console.error("Error fetching cars:", error);
  }
  return variantsCount;
}
